// Returns a getter that builds a text summary of the current report state (name, subjects, block titles,
// and any loaded analysis data) to include as context for Claude.
// We reuse the excel export functions (viewExcelFunctions) because they are the only existing uniform
// interface that extracts rendered data from all block types without per-block-type logic.
// TODO: Investigate a more direct data extraction approach that doesn't couple to the excel pipeline.
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
  analysisViewNameState,
  allBlockIdsState,
  allSubjectsSelector,
  blockDisplayHeader,
  blockExcelFunction,
} from 'venn-state';
import type { ExcelCell } from 'venn-utils';
import { MAX_ROWS_PER_BLOCK } from './wordBudget';

export const excelCellsToText = (rows: ExcelCell[][]): string => {
  const body = rows
    .slice(0, MAX_ROWS_PER_BLOCK)
    .map((row) =>
      row
        .map((cell) => {
          if (cell == null || cell.value == null) return '';
          if (cell.percentage && typeof cell.value === 'number') {
            return `${(cell.value * 100).toFixed(2)}%`;
          }
          if (cell.currency && typeof cell.value === 'number') {
            return `${cell.currencySymbol ?? '$'}${cell.value.toFixed(2)}`;
          }
          if (typeof cell.value === 'number') {
            return cell.digits != null ? cell.value.toFixed(cell.digits) : String(cell.value);
          }
          return String(cell.value);
        })
        .join(' | '),
    )
    .filter((line) => line.trim())
    .join('\n');
  if (rows.length <= MAX_ROWS_PER_BLOCK) return body;
  return `${body}\n(Truncated: showing first ${MAX_ROWS_PER_BLOCK} of ${rows.length} rows. Method: first-N rows in display order.)`;
};

const useReportContext = (): (() => { context: string; hasTruncatedBlocks: boolean }) => {
  const reportName = useRecoilValue(analysisViewNameState);
  const blockIds = useRecoilValue(allBlockIdsState);
  const subjects = useRecoilValue(allSubjectsSelector);

  const getBlockData = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const titles: string[] = [];
        for (const id of blockIds) {
          try {
            const title = snapshot.getLoadable(blockDisplayHeader(id)).getValue();
            if (title) titles.push(title);
          } catch {
            // Skip blocks whose title can't be resolved
          }
        }

        const blockDataSections: string[] = [];
        let hasTruncatedBlocks = false;
        for (const [i, id] of blockIds.entries()) {
          const loadable = snapshot.getLoadable(blockExcelFunction(id));
          if (loadable.state !== 'hasValue') continue;
          const fn = loadable.getValue();
          if (!fn?.excelFunction) continue;
          const data = fn.excelFunction();
          if (data && data.length > 0) {
            if (data.length > MAX_ROWS_PER_BLOCK) hasTruncatedBlocks = true;
            const title = titles[i] ?? `Block ${i + 1}`;
            const text = excelCellsToText(data);
            if (text) {
              blockDataSections.push(`[${title}]\n${text}`);
            }
          }
        }

        return { titles, blockDataSections, hasTruncatedBlocks };
      },
    [blockIds],
  );

  return useRecoilCallback(
    () => () => {
      const sections: string[] = [];

      if (reportName) {
        sections.push(`Report Name: "${reportName}"`);
      }

      if (subjects.length > 0) {
        const subjectList = subjects
          .map((s) => {
            const type = s.private ? 'Private ' : '';
            const subjectType = 'portfolio' in s && s.portfolio ? 'Portfolio' : 'Investment';
            return `- ${s.name} (${type}${subjectType})`;
          })
          .join('\n');
        sections.push(`Subjects (portfolios/investments being analyzed):\n${subjectList}`);
      }

      const { titles, blockDataSections, hasTruncatedBlocks } = getBlockData();

      if (titles.length > 0) {
        sections.push(`Report Blocks (${titles.length} total):\n${titles.map((t) => `- ${t}`).join('\n')}`);
      }

      if (blockDataSections.length > 0) {
        sections.push(`Block Data:\n${blockDataSections.join('\n\n')}`);
      }

      const context = sections.length === 0 ? '' : sections.join('\n\n');
      return { context, hasTruncatedBlocks };
    },
    [reportName, subjects, getBlockData],
  );
};

export default useReportContext;
